import { IHousingObjectResponse } from '@/types';

/**
 * Builds the GA4 `object_inquiry` dataLayer payload from listing API data after a successful
 * enquiry (form or WhatsApp). Normalises category, price, area, rooms, and location fields.
 */

function parseFirstNumber(value: string | undefined | null): number | undefined {
  if (!value) return undefined;
  const m = String(value).match(/[\d]+(?:[.,]\d+)?/);
  if (!m) return undefined;
  const n = Number(m[0].replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

function parsePriceNumber(value: string | undefined | null): number | undefined {
  if (!value) return undefined;
  const cleaned = String(value).replace(/[^\d]/g, '');
  if (!cleaned) return undefined;
  const n = parseInt(cleaned, 10);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Splits a combined "ZIP + location" string (e.g. from free text) into `object_zip` and `object_location`.
 * Supports `12345 City`, ZIP-only, or location-only input.
 */
export function parseZipLocationFromCombined(combined: string): { object_zip: string; object_location: string } {
  const trimmed = combined.trim();
  if (!trimmed) return { object_zip: '', object_location: '' };
  const m = trimmed.match(/^(\d{4,5})\s+(.+)$/);
  if (m) return { object_zip: m[1], object_location: m[2].trim() };
  if (/^\d{4,5}$/.test(trimmed)) return { object_zip: trimmed, object_location: '' };
  return { object_zip: '', object_location: trimmed };
}

function mapObjectCategory(data: IHousingObjectResponse): string {
  const raw = `${data.categoryTitle || ''} ${data.categoryNameTitle || ''}`.toLowerCase();
  if (raw.includes('grundstück') || raw.includes('grundstuck')) return 'land';
  if (raw.includes('haus')) return 'house';
  return 'apartment';
}

/**
 * Returns flat fields for `window.dataLayer.push({ event: 'object_inquiry', ... })`.
 * Includes a dynamic key `'object_zip - object_location'` with the combined ZIP and place string.
 */
export function buildObjectInquiryPayload(
  data: IHousingObjectResponse,
  housingId: string,
  inquiry_type: 'Form' | 'WhatsApp',
): Record<string, unknown> {
  const isSell = Boolean(data.sell);
  const priceRaw = isSell ? data.prices?.sellPrice : data.prices?.grossRent;
  const object_price = parsePriceNumber(priceRaw);
  const areaStr = data.squares?.livingArea || data.squares?.areaDetailed;
  const object_area = parseFirstNumber(areaStr);

  const rooms = data.otherDetails?.roomNumber;
  const object_rooms =
    typeof rooms === 'number'
      ? rooms
      : typeof rooms === 'string'
        ? parseFirstNumber(rooms)
        : undefined;

  const object_zip = (data.postcode || '').trim();
  const object_location = (data.place || '').trim();
  const object_zip_location_combined = [object_zip, object_location].filter(Boolean).join(' ').trim();

  const objectZipLocationKey = 'object_zip - object_location';

  return {
    object_id: housingId,
    object_type: isSell ? 'purchase' : 'rent',
    object_category: mapObjectCategory(data),
    object_price,
    object_area,
    object_rooms,
    object_zip,
    object_location,
    [objectZipLocationKey]: object_zip_location_combined,
    inquiry_type,
  };
}
